import * as React from 'react'
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import styled from '@emotion/styled'
import { cascadeDisplayName } from './cascade_utils'
import { CascadeSummary } from './CascadeSummary'
import { useCascadeStandards } from './hooks'

const SectionPaper = styled(Paper)(() => ({
  padding: '24px',
  marginBottom: '24px',
}))

interface CascadeStandardsProps {
  treeId: string
  versionId: string
}

export function CascadeStandards({ treeId, versionId }: CascadeStandardsProps) {
  // cascadeDataAtom is now a derived atom that automatically recalculates when cascade settings change
  const {
    fromType,
    toType,
    selectedSkillsListTitle,
    skillsListTitles,
    fromTypes,
    toTypes,
    mode,
    isToTypeValid,
    hasSkills,
    isTypeInDocument,
    handleFromTypeChange,
    handleToTypeChange,
    handleSkillsListTitleChange,
    handleModeChange,
  } = useCascadeStandards(treeId, versionId)

  return (
    <SectionPaper>
      <Typography variant="h6" gutterBottom>
        Cascade Standards from Google Sheet
      </Typography>
      <Box sx={{ mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Mode</InputLabel>
          <Select
            value={mode}
            onChange={(e) => handleModeChange(e.target.value)}
            label="Mode"
          >
            <MenuItem value="Off">Off</MenuItem>
            <MenuItem value="Roll Up">Roll Up</MenuItem>
            <MenuItem value="Roll Down">Roll Down</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            From (one)
          </Typography>
          <RadioGroup
            value={fromType}
            onChange={(e) => handleFromTypeChange(e.target.value)}
            sx={{
              '& .MuiFormControlLabel-root': {
                mb: 0,
                mt: 0,
                py: 0,
                minHeight: 'auto',
              },
            }}
          >
            {fromTypes.map((type) => {
              const isNotInDocument = !isTypeInDocument(type)
              const hasNoSkills = !hasSkills(type)
              const isDisabled = isNotInDocument || hasNoSkills
              return (
                <FormControlLabel
                  key={type}
                  value={type}
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: isDisabled ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {cascadeDisplayName(type)}
                    </Typography>
                  }
                  disabled={isDisabled}
                  sx={{ mb: 0, mt: 0, py: 0, gap: 0.25 }}
                />
              )
            })}
          </RadioGroup>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            To (one)
          </Typography>
          <RadioGroup
            value={toType}
            onChange={(e) => handleToTypeChange(e.target.value)}
            sx={{
              '& .MuiFormControlLabel-root': {
                mb: 0,
                mt: 0,
                py: 0,
                minHeight: 'auto',
              },
            }}
          >
            {toTypes.map((type) => {
              const isNotInDocument = !isTypeInDocument(type)
              const isSameAsFrom = fromType === type
              const isInvalidDirection = Boolean(
                fromType &&
                  mode !== 'Off' &&
                  !isToTypeValid(type, fromType, mode),
              )
              const isDisabled =
                isNotInDocument || isSameAsFrom || isInvalidDirection
              return (
                <FormControlLabel
                  key={type}
                  value={type}
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: isDisabled ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {cascadeDisplayName(type)}
                    </Typography>
                  }
                  disabled={isDisabled}
                  sx={{ mb: 0, mt: 0, py: 0, gap: 0.25 }}
                />
              )
            })}
          </RadioGroup>
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
          Skills List Title (one)
        </Typography>
        <RadioGroup
          value={selectedSkillsListTitle}
          onChange={(e) => handleSkillsListTitleChange(e.target.value)}
          row
          sx={{
            gap: 1,
            '& .MuiFormControlLabel-root': {
              mb: 0,
              mt: 0,
              py: 0,
              minHeight: 'auto',
              mr: 1,
            },
          }}
        >
          {skillsListTitles.map((title) => (
            <FormControlLabel
              key={title}
              value={title}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {title}
                </Typography>
              }
              sx={{ mb: 0, mt: 0, py: 0, gap: 0.25, mr: 0 }}
            />
          ))}
        </RadioGroup>
      </Box>
      <CascadeSummary />
    </SectionPaper>
  )
}
